import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { ListingLinkButton } from "@/components/common/Primitives";
import { MarketValueDialog } from "@/components/valuation/MarketValueDialog";
import { RiskDialog } from "@/components/risks/RiskDialog";
import { BpmOverrideDialog } from "@/components/bpm/BpmOverrideDialog";
import { BpmDialog, CostDialog, ScoreDialog, ValuationDialog } from "@/components/explain/ExplainDialogs";
import { api, apiError } from "@/lib/api";
import { countryName, dateOnly, km, vehicleTitle } from "@/lib/format";
import { GalleryPanel, VehicleSpecsPanel } from "@/components/vehicle/InfoPanels";
import {
  ComparablesPanel,
  PriceHistoryPanel,
  WaterfallPanel,
  buildPriceHistory,
} from "@/components/vehicle/FinancialPanels";
import {
  BpmPanel,
  MaxPurchasePanel,
  ScenariosPanel,
  ScorePanel,
  ValuesPanel,
} from "@/components/vehicle/SummaryPanels";
import { Co2Panel } from "@/components/vehicle/Co2Panel";
import {
  AiPanel,
  AuditPanel,
  OverridesPanel,
  RedFlagsPanel,
} from "@/components/vehicle/AdjustmentPanels";

export default function VehicleDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [scenario, setScenario] = useState("base");
  const [dialog, setDialog] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");
  const [overrides, setOverrides] = useState({});
  const [co2, setCo2] = useState("");
  const [vat, setVat] = useState("");
  const [required, setRequired] = useState("");
  const [gallery, setGallery] = useState(0);
  const [busy, setBusy] = useState(false);
  const [co2Busy, setCo2Busy] = useState("");
  const [co2Msg, setCo2Msg] = useState("");
  const [manual, setManual] = useState({
    co2_value: "", measurement_method: "WLTP", source_name: "", source_url: "", note: "",
  });
  const [showManual, setShowManual] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data: payload } = await api.get(`/listings/${id}`);
      setData(payload);
    } catch (e) {
      setError(apiError(e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const listing = data?.listing;
  const analysis = data?.analysis;
  const head = analysis?.headline || {};
  const chosen = analysis?.scenarios?.[scenario] || {};
  const costs = scenario === "conservative" ? analysis?.conservative_costs : analysis?.costs;
  const maxPurchase = chosen?.max_purchase_price;
  const watch = data?.watchlist;

  const recalc = async () => {
    setBusy(true);
    try {
      const body = {};
      const cleanOverrides = Object.fromEntries(
        Object.entries(overrides).filter(([, v]) => v !== "" && v !== null),
      );
      if (Object.keys(cleanOverrides).length) body.cost_overrides = cleanOverrides;
      if (co2 !== "") body.co2_wltp = Number(co2);
      if (vat !== "") body.vat_status = vat;
      if (required !== "") body.required_contribution = Number(required);
      await api.post(`/listings/${id}/recalculate`, body);
      await load();
    } catch (e) {
      setError(apiError(e));
    }
    setBusy(false);
  };

  const enrichCo2 = async (force) => {
    setCo2Busy("enrich");
    setCo2Msg("");
    try {
      const { data: res } = await api.post(
        `/listings/${id}/co2-enrich?force=${force ? "true" : "false"}`,
      );
      setCo2Msg(
        res.co2?.usable
          ? `CO₂ gevonden: ${res.co2.co2} g/km (${res.co2.measurement_method}) via ` +
              `${res.co2.source_name} — ${res.co2.confidence_label} ${Math.round(res.co2.confidence)}%.`
          : "Geen betrouwbare CO₂-bron gevonden. Er wordt géén waarde ingevuld.",
      );
      await load();
    } catch (e) {
      setCo2Msg(apiError(e));
    }
    setCo2Busy("");
  };

  const saveManualCo2 = async () => {
    setCo2Busy("manual");
    setCo2Msg("");
    try {
      await api.post(`/listings/${id}/co2-manual`, {
        co2_value: Number(manual.co2_value),
        measurement_method: manual.measurement_method,
        source_name: manual.source_name || "Handmatige invoer",
        source_url: manual.source_url || null,
        note: manual.note || null,
      });
      setShowManual(false);
      setCo2Msg("Handmatige CO₂-waarde opgeslagen en gelabeld als MANUAL OVERRIDE.");
      await load();
    } catch (e) {
      setCo2Msg(apiError(e));
    }
    setCo2Busy("");
  };

  const runAi = async () => {
    setAiBusy(true);
    setAiError("");
    try {
      await api.post(`/listings/${id}/ai-analysis`);
      await load();
    } catch (e) {
      setAiError(apiError(e));
    }
    setAiBusy(false);
  };

  const toggleWatch = async () => {
    if (watch) {
      await api.delete(`/watchlist/${id}`);
    } else {
      await api.post("/watchlist", {
        listing_id: id,
        status: "Nieuw",
        target_buy_price: maxPurchase?.max_purchase_price ?? null,
        max_purchase_price: maxPurchase?.max_purchase_price ?? null,
      });
    }
    load();
  };

  if (error) {
    return (
      <AppShell title="Auto Detail">
        <div className="rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800">
          {error}
        </div>
      </AppShell>
    );
  }
  if (!data) {
    return (
      <AppShell title="Auto Detail">
        <div className="panel px-4 py-10 text-center text-[13px] text-slate-500">Laden…</div>
      </AppShell>
    );
  }

  const priceHistory = buildPriceHistory(data.price_history);
  const flags = analysis?.red_flags || {};
  const subtitle = [
    listing.generation,
    dateOnly(listing.first_registration),
    km(listing.mileage_km),
    countryName(listing.country),
  ]
    .filter(Boolean)
    .join(" | ");
  const freshness = [
    { label: "AutoScout data bijgewerkt", value: analysis?.timestamps?.source_data_updated },
    { label: "NL marktvergelijking", value: analysis?.timestamps?.comparables_calculated },
    { label: "BPM berekend", value: analysis?.timestamps?.bpm_calculated },
  ];

  return (
    <AppShell
      title={vehicleTitle(listing)}
      subtitle={subtitle}
      freshness={freshness}
      actions={
        <>
          <button
            type="button"
            onClick={toggleWatch}
            data-testid="watchlist-toggle"
            className={`flex items-center gap-1.5 rounded-sm border px-2.5 py-1.5 text-[12px] font-semibold transition-colors duration-150 ${
              watch
                ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            {watch ? <BookmarkCheck className="h-3.5 w-3.5" /> : <Bookmark className="h-3.5 w-3.5" />}
            {watch ? "Op watchlist" : "Aan watchlist"}
          </button>
          <ListingLinkButton url={listing.url} size="lg" testid="detail-listing-url" />
        </>
      }
    >
      <div className="grid gap-2 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-2">
          <GalleryPanel images={listing.image_urls} gallery={gallery} setGallery={setGallery} />
          <VehicleSpecsPanel listing={listing} />
          <WaterfallPanel
            costs={costs}
            chosen={chosen}
            scenario={scenario}
            onOpenCost={() => setDialog("cost")}
          />
          <ComparablesPanel
            analysis={analysis}
            head={head}
            listingId={id}
            onReload={load}
            onOpenValuation={() => setDialog("valuation")}
            onOpenMarketValue={() => setDialog("market-value")}
          />
          <PriceHistoryPanel priceHistory={priceHistory} listing={listing} />
        </div>

        <div className="min-w-0 space-y-2">
          <ScorePanel head={head} analysis={analysis} onOpenScore={() => setDialog("score")} />
          <ValuesPanel head={head} scenario={scenario} setScenario={setScenario} />
          <BpmPanel
            analysis={analysis}
            head={head}
            onOpenBpm={() => setDialog("bpm")}
            onOpenBpmOverride={() => setDialog("bpm-override")}
          />
          <Co2Panel
            analysis={analysis}
            co2Msg={co2Msg}
            co2Busy={co2Busy}
            enrichCo2={enrichCo2}
            manual={manual}
            setManual={setManual}
            showManual={showManual}
            setShowManual={setShowManual}
            saveManualCo2={saveManualCo2}
            onOpenBpm={() => setDialog("bpm")}
          />
          <MaxPurchasePanel maxPurchase={maxPurchase} />
          <ScenariosPanel analysis={analysis} scenario={scenario} />
          <OverridesPanel
            overrides={overrides}
            setOverrides={setOverrides}
            co2={co2}
            setCo2={setCo2}
            vat={vat}
            setVat={setVat}
            required={required}
            setRequired={setRequired}
            analysis={analysis}
            listing={listing}
            recalc={recalc}
            busy={busy}
          />
          <RedFlagsPanel flags={flags} onOpenRisk={() => setDialog("risk")} />
          <AiPanel aiData={data.ai_analysis} aiBusy={aiBusy} aiError={aiError} runAi={runAi} />
          <AuditPanel history={data.analysis_history} analysis={analysis} />

          <div className="px-1 text-[11px] text-slate-500">
            <Link to="/handelskansen" className="font-semibold text-blue-700 hover:underline">
              ← Terug naar handelskansen
            </Link>
          </div>
        </div>
      </div>

      <BpmDialog
        open={dialog === "bpm"}
        onOpenChange={() => setDialog(null)}
        bpm={analysis?.bpm}
        co2={analysis?.co2}
        listingId={id}
        onChanged={() => load()}
      />
      <MarketValueDialog
        open={dialog === "market-value"}
        onOpenChange={(open) => !open && setDialog(null)}
        row={analysis ? { ...analysis, listing_id: id } : null}
        onApplied={() => {
          setDialog(null);
          load();
        }}
      />
      <BpmOverrideDialog
        open={dialog === "bpm-override"}
        onOpenChange={(open) => !open && setDialog(null)}
        row={analysis ? { ...analysis, listing_id: id } : null}
        onApplied={() => {
          setDialog(null);
          load();
        }}
      />
      <ValuationDialog
        open={dialog === "valuation"}
        onOpenChange={() => setDialog(null)}
        valuation={analysis?.valuation}
        listingId={id}
        onChanged={() => load()}
      />
      <ScoreDialog open={dialog === "score"} onOpenChange={() => setDialog(null)} score={analysis?.deal_score} />
      <CostDialog open={dialog === "cost"} onOpenChange={() => setDialog(null)} costs={costs} scenario={chosen} />
      <RiskDialog open={dialog === "risk"} onOpenChange={() => setDialog(null)} flags={analysis?.red_flags} />
    </AppShell>
  );
}
